<!--
  ConfirmButton.svelte — Double-click confirmation button for critical actions.

  Wraps a flowbite-svelte Button. On click, opens a confirmation modal.
  If the user confirms, calls `onconfirm`. If `requireConfirmation`
  is false, calls immediately without the modal.

  Usage:
    <ConfirmButton action="delete" color="red" onconfirm={handleDelete}>
      <i class="fa-solid fa-trash me-1"></i>Delete
    </ConfirmButton>
-->
<script lang="ts">
	import { Button, Modal } from 'flowbite-svelte';
	import type { ButtonProps } from 'flowbite-svelte';
	import type { Snippet } from 'svelte';
	import { t } from '../i18n';

	interface Props {
		/** Label for the action shown in the confirmation prompt (e.g. "delete", "send"). */
		action?: string;
		/** Custom confirmation prompt text. */
		confirmText?: string;
		/** Whether the button is disabled. */
		disabled?: boolean | undefined;
		/** Whether the button is pill-shaped. */
		pill?: boolean | undefined;
		/** Whether the button has an outline style. */
		outline?: boolean | undefined;
		/** Button size. */
		size?: 'xs' | 'sm' | 'lg' | 'xl' | 'md' | undefined;
		/** Button colour variant. */
		color?: ButtonProps['color'];
		/** Colour for the confirm button inside the modal (defaults to 'red'). */
		confirmColor?: ButtonProps['color'];
		/** Custom CSS classes for the button. */
		class?: string;
		/** When false, fires `onconfirm` immediately without the modal. */
		requireConfirmation?: boolean;
		/** Callback fired when the user confirms the action. */
		onconfirm?: (() => void) | undefined;
		/** Button content. */
		children?: Snippet;
	}

	let {
		action = 'proceed',
		confirmText,
		disabled = false,
		pill = undefined,
		outline = undefined,
		size = 'md' as 'xs' | 'sm' | 'lg' | 'xl' | 'md' | undefined,
		color = undefined,
		confirmColor = 'red',
		class: className = '',
		requireConfirmation = true,
		onconfirm = undefined,
		children
	}: Props = $props();

	/** Resolved confirmation prompt text. */
	let resolvedConfirmText = $derived(confirmText ?? $t('Are you sure you want to {{action}}?', { action }));

	/** Whether the confirmation modal is open. */
	let showModal = $state(false);

	/** Accept the dialog and fire the action. */
	function fnConfirm(): void {
		showModal = false;
		onconfirm?.();
	}

	/*
	 * Enter accepts the dialog. The listener is bound to the window rather than
	 * the modal because focus sits on whichever button the user last touched,
	 * and it is only active while the dialog is open — a page listing many rows
	 * would otherwise accumulate one permanent listener per button.
	 */
	/** Window key handler backing the Enter-accepts shortcut above. */
	function fnOnWindowKeyDown(event: KeyboardEvent): void {
		if (!showModal) return;
		if (event.key !== 'Enter') return;
		event.preventDefault();
		fnConfirm();
	}
</script>

<svelte:window onkeydown={fnOnWindowKeyDown} />

<Modal bind:open={showModal} size="xs">
	<div class="text-center">
		<i class="fa-solid fa-triangle-exclamation fa-3x mb-4 text-yellow-500"></i>
		<h3 class="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">{resolvedConfirmText}</h3>
		<div class="flex items-center justify-between">
			<Button onclick={() => (showModal = false)} color="alternative">{$t("No, cancel")}</Button>
			<Button onclick={fnConfirm} color={confirmColor}>{$t("Yes, I'm sure")}</Button>
		</div>
	</div>
</Modal>

<Button
	{disabled}
	class={className}
	{pill}
	{outline}
	{size}
	{color}
	onclick={() => {
		if (!requireConfirmation) {
			onconfirm?.();
			return;
		}
		showModal = true;
	}}
>
	{@render children?.()}
</Button>
